import { Box, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, Cell, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { siteLookupTokens } from '../siteLookupTokens';
import { ChargingPriceChart, ChargingPriceWeek } from './ChargingPriceChart';
import { getMonthSegments, HistogramBucket } from './HistoricalViewContainer';
import { priceAxis } from './priceBands';

const { chart, text } = siteLookupTokens.color;

const CHART_MARGIN = { top: 4, right: 4, left: 0, bottom: 0 };
const Y_AXIS_WIDTH = 40;
const axisTick = { fontSize: 11, fill: text.muted };
const tooltipStyle = { contentStyle: { fontSize: 12, padding: '4px 8px', borderRadius: 6 }, labelStyle: { marginBottom: 2 } };

/** Title row of a sheet chart: the title left, a summary (average, range, current hour) right. */
export const ChartTitle = ({ title, aside }: { title: string; aside?: ReactNode }) => (
  <Box display="flex" alignItems="baseline" justifyContent="space-between" gap={1} marginBottom={0.75}>
    <Typography component="h3" fontSize={13} fontWeight={600} color={text.primary}>
      {title}
    </Typography>
    {aside && (
      <Typography fontSize={12} color={text.secondary}>
        {aside}
      </Typography>
    )}
  </Box>
);

const percent = (value: number) => `${value}%`;

const formatWeek = (endDate: string, locale: string) => new Date(endDate).toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' });

const money = (value: number) => `$${value.toFixed(2)}`;

type WeekDatum = {
  index: number;
  endDate: string;
  utilization: number | null;
  /** the API weekly average of the station sheet's price line */
  price: number | null;
};

type WeeklyProps = {
  buckets: HistogramBucket[];
  /**
   * Area Summary: the price chart is the ChargingPriceChart of the station prices, its weekly median
   * (medianPrice) over their middle 50% (p25Price–p75Price), with min–max (minPrice–maxPrice) in its tooltip.
   */
  showPriceSpread?: boolean;
};

/**
 * Historical sheet: weekly utilization bars and a separate price per kWh chart (one axis each). On the station
 * sheet both share the month axis below the price chart; in the Area Summary the charging price chart has its own
 * months, so the utilization bars get theirs right below them. Every week, the last included, is drawn the same
 * way. The price axis is padded and rounded so that a few cents do not fill its height.
 */
export const WeeklyStationCharts = ({ buckets, showPriceSpread = false }: WeeklyProps) => {
  const { t, i18n } = useTranslation();

  const data = useMemo(
    () =>
      buckets.map(
        (b, index): WeekDatum => ({
          index,
          endDate: b.endDate,
          utilization: b.percentageUtilization === undefined ? null : Math.max(0, Math.min(100, Math.round(b.percentageUtilization * 100))),
          price: showPriceSpread ? null : (b.price ?? null),
        }),
      ),
    [buckets, showPriceSpread],
  );
  const priceWeeks = useMemo(
    () =>
      buckets.map(
        (b): ChargingPriceWeek => ({ weekEnding: b.endDate, median: b.medianPrice, p25: b.p25Price, p75: b.p75Price, min: b.minPrice, max: b.maxPrice }),
      ),
    [buckets],
  );

  const utilizations = data.map((d) => d.utilization).filter((u): u is number => u !== null);
  const prices = data.map((d) => d.price).filter((p): p is number => p !== null);
  const hasPrices = showPriceSpread ? priceWeeks.some((w) => w.median !== undefined) : prices.length > 0;
  const axis = priceAxis(prices);
  const average = utilizations.length ? Math.round(utilizations.reduce((a, b) => a + b, 0) / utilizations.length) : null;
  const months = useMemo(
    () =>
      getMonthSegments(
        data.map((d) => d.endDate),
        i18n.language,
      ),
    [data, i18n.language],
  );

  if (!utilizations.length && !hasPrices) {
    return (
      <Typography fontSize={14} color={text.secondary} paddingY={2}>
        {t('siteLookup.detail.historical.noData')}
      </Typography>
    );
  }

  const labelFormatter = (_: unknown, payload: readonly { payload?: { endDate?: string } }[]) =>
    payload?.[0]?.payload?.endDate ? t('siteLookup.detail.historical.weekEnding', { date: formatWeek(payload[0].payload.endDate, i18n.language) }) : '';

  const monthAxis = (
    <Box
      aria-hidden
      display="grid"
      gridTemplateColumns={months.map((m) => `${m.count}fr`).join(' ')}
      paddingLeft={`${Y_AXIS_WIDTH + CHART_MARGIN.left}px`}
      paddingRight={`${CHART_MARGIN.right}px`}
      marginTop={0.5}
    >
      {months.map((m) => (
        <Typography key={m.key} fontSize={11} color={text.muted} textAlign="center">
          {m.label}
        </Typography>
      ))}
    </Box>
  );

  return (
    <Box>
      <ChartTitle
        title={t('siteLookup.detail.historical.weeklyUtilization')}
        aside={average !== null && t('siteLookup.detail.historical.average', { value: average })}
      />
      <Box height={120}>
        <ResponsiveContainer>
          <BarChart data={data} margin={CHART_MARGIN} barCategoryGap={2}>
            <CartesianGrid vertical={false} stroke={chart.grid} />
            <XAxis dataKey="index" hide />
            <YAxis domain={[0, 100]} ticks={[0, 50, 100]} tickFormatter={percent} width={Y_AXIS_WIDTH} tick={axisTick} axisLine={false} tickLine={false} />
            <Tooltip
              {...tooltipStyle}
              labelFormatter={labelFormatter}
              formatter={(value) => [percent(Number(value)), t('siteLookup.detail.historical.utilization')]}
            />
            <Bar dataKey="utilization" fill={chart.bar} isAnimationActive={false} radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Box>

      {showPriceSpread ? (
        <>
          {monthAxis}
          <Box marginTop={2}>
            <ChargingPriceChart weeks={priceWeeks} />
          </Box>
        </>
      ) : (
        <>
          <Box marginTop={2}>
            <ChartTitle
              title={t('siteLookup.detail.historical.pricePerKwh')}
              aside={prices.length > 0 && t('siteLookup.detail.historical.priceRange', { min: money(Math.min(...prices)), max: money(Math.max(...prices)) })}
            />
            <Box height={90}>
              <ResponsiveContainer>
                <ComposedChart data={data} margin={CHART_MARGIN}>
                  <CartesianGrid vertical={false} stroke={chart.grid} />
                  <XAxis dataKey="index" hide />
                  <YAxis
                    domain={axis?.domain ?? ['auto', 'auto']}
                    ticks={axis?.ticks}
                    tickFormatter={money}
                    width={Y_AXIS_WIDTH}
                    tick={axisTick}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    {...tooltipStyle}
                    labelFormatter={labelFormatter}
                    formatter={(value) => [money(Number(value)), t('siteLookup.detail.historical.price')]}
                  />
                  <Line type="linear" dataKey="price" stroke={chart.line} strokeWidth={2} dot={false} connectNulls isAnimationActive={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          </Box>
          {monthAxis}
        </>
      )}
    </Box>
  );
};

type HourlyProps = {
  /** share of each hour (0–23) spent charging, from 0 to 1 */
  hourly: number[];
  /** current hour in the station's timezone */
  currentHour: number;
};

const formatHour = (hour: number, locale: string) => new Intl.DateTimeFormat(locale, { hour: 'numeric', timeZone: 'UTC' }).format(Date.UTC(2000, 0, 1, hour));

/** Real-time sheet: typical utilization of the 24 hours, the current hour darker. */
export const HourlyUtilizationChart = ({ hourly, currentHour }: HourlyProps) => {
  const { t, i18n } = useTranslation();
  const data = hourly.map((ratio, hour) => ({ hour, utilization: Math.round(ratio * 100) }));

  return (
    <Box>
      <ChartTitle
        title={t('siteLookup.detail.realtime.typicalUtilization')}
        aside={t('siteLookup.detail.realtime.now', { hour: formatHour(currentHour, i18n.language) })}
      />
      <Box height={110}>
        <ResponsiveContainer>
          <BarChart data={data} margin={CHART_MARGIN} barCategoryGap={2}>
            <CartesianGrid vertical={false} stroke={chart.grid} />
            <XAxis
              dataKey="hour"
              ticks={[0, 6, 12, 18]}
              interval={0}
              tickFormatter={(hour) => formatHour(Number(hour), i18n.language)}
              tick={axisTick}
              axisLine={false}
              tickLine={false}
            />
            <YAxis domain={[0, 100]} ticks={[0, 50, 100]} tickFormatter={percent} width={Y_AXIS_WIDTH} tick={axisTick} axisLine={false} tickLine={false} />
            <Tooltip
              {...tooltipStyle}
              labelFormatter={(hour) => formatHour(Number(hour), i18n.language)}
              formatter={(value) => [percent(Number(value)), t('siteLookup.detail.historical.utilization')]}
            />
            <Bar dataKey="utilization" isAnimationActive={false} radius={[2, 2, 0, 0]}>
              {data.map((d) => (
                <Cell key={d.hour} fill={d.hour === currentHour ? chart.barCurrent : chart.bar} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};
